import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from '@omilia/providers';
import useKeyboard from '@omilia/use-keyboard';
import {
  DownloadIcon,
  ExitIcon,
  RedoIcon,
  VolumeMuteIcon,
  VolumeUpIcon,
} from '@omilia/icon';
import { colors } from '@omilia/theme';

import { STORAGE_KEY } from 'constants/browserStorageKeys';
import { getStorageItem, setStorageItem } from 'services/storage';
import {
  getAgentData,
  getBotData,
  getChatTitle,
  getLocale,
  getNotificationsConfig,
} from 'store/services/session/selectors';
import { getThread } from 'store/services/chat/selectors';
import TranscriptPDF from 'components/TranscriptPDF';
import { getPdfOwnerNameForTranscript } from 'utils/getPdfOwnerNameForTranscript';
import {
  SDivider,
  SMenuEndChatItem,
  SMenuEndChatText,
  SMenuItem,
  SMenuItemGroup,
  SMenuItemText,
  SMenuOverlay,
  STranscriptInner,
  STranscriptLink,
} from './ContextMenu.styled';

interface ContextMenuProps {
  isOpen: boolean;
  isRestartEnabled: boolean;
  enableTranscriptDownload: boolean;
  onRestartChat: () => void;
  onEndChat: () => void;
  onClose: () => void;
}

const ContextMenu = ({
  isOpen,
  isRestartEnabled,
  enableTranscriptDownload,
  onRestartChat,
  onEndChat,
  onClose,
}: ContextMenuProps) => {
  const { t } = useTranslation();
  const locale = useSelector(getLocale);
  const thread = useSelector(getThread);
  const agent = useSelector(getAgentData);
  const bot = useSelector(getBotData);
  const title = useSelector(getChatTitle);
  const { withSound } = useSelector(getNotificationsConfig);
  const [isMutedSound, setMuted] = useState(() =>
    Boolean(getStorageItem(STORAGE_KEY.MUTE_SOUND)),
  );

  const handleRestartChat = () => {
    onRestartChat();
    onClose();
  };

  const handleSoundToggle = () => {
    const nextMuted = !isMutedSound;
    setMuted(nextMuted);
    setStorageItem(STORAGE_KEY.MUTE_SOUND, nextMuted);
    onClose();
  };

  const handleEndChat = () => {
    onEndChat();
    onClose();
  };

  const soundLabel = isMutedSound
    ? t('sound.enableSound')
    : t('sound.disableSound');
  const SoundIcon = isMutedSound ? VolumeUpIcon : VolumeMuteIcon;

  const transcriptOwnerLabels = useMemo(
    () => ({
      user: t('messages.user'),
      agent: t('messages.agent'),
      bot: t('messages.bot'),
    }),
    [t],
  );

  const threadForTranscript = useMemo(
    () =>
      thread.map(item => ({
        ...item,
        pdfOwnerName:
          item.pdfOwnerName ??
          getPdfOwnerNameForTranscript(item, agent, bot, transcriptOwnerLabels),
      })),
    [thread, agent, bot, transcriptOwnerLabels],
  );

  const hasPrimarySection =
    isRestartEnabled || withSound || enableTranscriptDownload;

  const keyboardProps = useKeyboard({
    role: 'menu',
    tabIndex: -1,
    handlers: {
      Escape: () => {
        onClose();
      },
    },
  });

  return (
    <SMenuOverlay
      $isOpen={isOpen}
      aria-hidden={!isOpen}
      data-test="context-menu"
      {...keyboardProps}
    >
      {hasPrimarySection && (
        <>
          <SMenuItemGroup>
            {isRestartEnabled && (
              <SMenuItem
                role="menuitem"
                onClick={handleRestartChat}
                data-test="menu-restart-chat"
                aria-label={t('header.restartChat')}
              >
                <RedoIcon size="16" color={colors.COLOR_GREY_900} />
                <SMenuItemText>{t('header.restartChat')}</SMenuItemText>
              </SMenuItem>
            )}
            {withSound && (
              <SMenuItem
                role="menuitem"
                onClick={handleSoundToggle}
                data-test="menu-sound-notifications"
                aria-label={soundLabel}
              >
                <SoundIcon size="16" primaryColor={colors.COLOR_GREY_900} />
                <SMenuItemText>{soundLabel}</SMenuItemText>
              </SMenuItem>
            )}
            {enableTranscriptDownload && (
              <STranscriptLink
                document={
                  <TranscriptPDF
                    thread={threadForTranscript}
                    title={title}
                    locale={locale}
                  />
                }
                fileName={t('files.fileName')}
                onClick={onClose}
              >
                <STranscriptInner data-test="menu-transcript">
                  <DownloadIcon size="16" color={colors.COLOR_GREY_900} />
                  <SMenuItemText>
                    {t('session.downloadTranscript')}
                  </SMenuItemText>
                </STranscriptInner>
              </STranscriptLink>
            )}
          </SMenuItemGroup>
          <SDivider aria-hidden />
        </>
      )}
      <SMenuEndChatItem
        role="menuitem"
        onClick={handleEndChat}
        data-test="menu-end-chat"
        aria-label={t('header.endChat')}
      >
        <ExitIcon size="16" primaryColor={colors.COLOR_RED_800} />
        <SMenuEndChatText>{t('header.endChat')}</SMenuEndChatText>
      </SMenuEndChatItem>
    </SMenuOverlay>
  );
};

export default ContextMenu;
